package hk.com.vr360_demo2.webapp.component.baseview

import emotion.react.css
import hk.com.vr360_demo2.webapp.component.useToast
import hk.com.vr360_demo2.webapp.core.ui_state.NetworkState
import hk.com.vr360_demo2.webapp.core.ui_state.ProgressBarState
import hk.com.vr360_demo2.webapp.core.ui_state.Queue
import hk.com.vr360_demo2.webapp.core.ui_state.UIComponent
import js.coroutines.awaitCancellation
import kotlinx.coroutines.flow.Flow
import mui.material.Button
import mui.material.CircularProgress
import mui.material.Dialog
import mui.material.DialogActions
import mui.material.DialogContent
import mui.material.DialogTitle
import react.FC
import react.PropsWithChildren
import react.dom.html.ReactHTML.div
import react.useEffect
import react.useState
import web.cssom.AlignItems
import web.cssom.Background
import web.cssom.Border
import web.cssom.Color
import web.cssom.Display
import web.cssom.FlexDirection
import web.cssom.JustifyContent
import web.cssom.LineStyle
import web.cssom.Padding
import web.cssom.Position
import web.cssom.ZIndex
import web.cssom.pct
import web.cssom.px
import web.cssom.vh
import web.cssom.vw
import kotlinx.browser.window
import kotlin.js.js
import kotlin.js.unsafeCast

external interface DefaultScreenUIProps : PropsWithChildren {
    var onTryAgain: (() -> Unit)?
    var errors: Flow<UIComponent>?
    var progressBarState: ProgressBarState?
    var networkState: NetworkState?
    var hasReady: Boolean?
    var emptyMessage: String?
    var loadingMessage: String?
}

val DefaultScreenUI = FC<DefaultScreenUIProps> { props ->
    val toast = useToast()
    val progressBarState = props.progressBarState ?: ProgressBarState.Idle
    val networkState = props.networkState ?: NetworkState.Good
    val hasReady = props.hasReady ?: true
    val onTryAgain = props.onTryAgain ?: {}
    val emptyMessage = props.emptyMessage ?: "暫無數據"
    val loadingMessage = props.loadingMessage ?: "正在加載.."

    val (isLoading, setIsLoading) = useState(false)
    val (errorQueue, setErrorQueue) = useState(Queue(mutableListOf<UIComponent>()))

    fun uiComponentKey(uiComponent: UIComponent): String = when (uiComponent) {
        is UIComponent.AlertDialog -> "alert:${uiComponent.title}:${uiComponent.message}"
        is UIComponent.DialogSimple -> "dialog:${uiComponent.title}:${uiComponent.description}"
        is UIComponent.ToastSimple -> "toast:${uiComponent.severity}:${uiComponent.message}"
        is UIComponent.TripleButtonDialog -> "triple:${uiComponent.title}:${uiComponent.message}"
        is UIComponent.None -> "none:${uiComponent.message}"
    }

    fun appendToMessageQueue(uiComponent: UIComponent) {
        if (uiComponent is UIComponent.None) return
        setErrorQueue { queue ->
            val lastItem = queue.toMutableList().lastOrNull()
            if (lastItem != null && uiComponentKey(lastItem) == uiComponentKey(uiComponent)) {
                queue
            } else {
                Queue(queue.toMutableList().apply { add(uiComponent) })
            }
        }
    }

    fun removeHeadMessage() {
        setErrorQueue { queue ->
            if (queue.isEmpty()) {
                queue
            } else {
                Queue(queue.toMutableList().apply { removeFirstOrNull() })
            }
        }
    }

    val uiComponent = errorQueue.peek()

    useEffect(progressBarState, hasReady) {
        if ((progressBarState is ProgressBarState.FullScreenLoading) && !hasReady) {
            setIsLoading(true)
        } else {
            val timerId = window.setTimeout({
                setIsLoading(false)
            }, 200)
            awaitCancellation { window.clearTimeout(timerId) }
        }
    }

    useEffect(props.errors) {
        val flow = props.errors ?: return@useEffect
        flow.collect { appendToMessageQueue(it) }
    }

    useEffect(uiComponent) {
        if (uiComponent is UIComponent.ToastSimple) {
            val severity = if (networkState is NetworkState.Failed) "error" else "success"
            toast(uiComponent.message, severity)
            removeHeadMessage()
        } else if (uiComponent is UIComponent.None) {
            removeHeadMessage()
        }
    }

    val isEmpty = progressBarState == ProgressBarState.Idle &&
        !hasReady &&
        networkState == NetworkState.Good

    div {
        css {
            position = Position.relative
            width = 100.pct
            height = 100.pct
        }

        if (hasReady) {
            div {
                css {
                    width = 100.pct
                    height = 100.pct
                }
                +props.children
            }
        }

        when (uiComponent) {
            is UIComponent.AlertDialog -> {
                Dialog {
                    open = true
                    this.fullWidth = true
                    asDynamic().maxWidth = "xs"
                    onClose = { _, _ -> removeHeadMessage() }

                    DialogTitle {
                        +(uiComponent.title.ifEmpty { "提示" })
                    }
                    DialogContent {
                        +uiComponent.message
                    }
                    DialogActions {
                        Button {
                            variant = mui.material.ButtonVariant.outlined
                            onClick = { removeHeadMessage() }
                            asDynamic().sx = js(
                                """{
                                    minWidth: '64px',
                                    width: '64px',
                                    minHeight: '28px',
                                    height: '28px',
                                    padding: '0',
                                    fontSize: '13px',
                                    lineHeight: 1,
                                    borderRadius: '4px',
                                    color: '#f26522',
                                    borderColor: '#f26522',
                                    backgroundColor: '#ffffff',
                                    '&:hover': {
                                        borderColor: '#f26522',
                                        backgroundColor: 'rgba(242,101,34,0.08)'
                                    }
                                }"""
                            )
                            +"取消"
                        }
                        Button {
                            variant = mui.material.ButtonVariant.contained
                            onClick = {
                                uiComponent.action?.invoke()
                                removeHeadMessage()
                            }
                            asDynamic().sx = js(
                                """{
                                    minWidth: '64px',
                                    width: '64px',
                                    minHeight: '28px',
                                    height: '28px',
                                    padding: '0',
                                    fontSize: '13px',
                                    lineHeight: 1,
                                    borderRadius: '4px',
                                    backgroundColor: '#f26522',
                                    color: '#ffffff',
                                    '&:hover': {
                                        backgroundColor: '#e45a1b'
                                    }
                                }"""
                            )
                            +"確定"
                        }
                    }
                }
            }

            is UIComponent.DialogSimple -> {
                Dialog {
                    open = true
                    this.fullWidth = true
                    asDynamic().maxWidth = "xs"
                    onClose = { _, _ -> removeHeadMessage() }

                    DialogTitle {
                        +uiComponent.title
                    }
                    DialogContent {
                        +uiComponent.description
                    }
                    DialogActions {
                        Button {
                            variant = mui.material.ButtonVariant.contained
                            onClick = { removeHeadMessage() }
                            +"確定"
                        }
                    }
                }
            }

            is UIComponent.TripleButtonDialog -> {
                Dialog {
                    open = true
                    this.fullWidth = true
                    asDynamic().maxWidth = "xs"
                    onClose = { _, _ -> removeHeadMessage() }

                    DialogTitle {
                        +(uiComponent.title ?: "提示")
                    }
                    DialogContent {
                        +uiComponent.message
                    }
                    DialogActions {
                        if (uiComponent.tertiaryButtonText != null) {
                            Button {
                                variant = mui.material.ButtonVariant.text
                                onClick = {
                                    uiComponent.onTertiaryButtonClick()
                                    removeHeadMessage()
                                }
                                +uiComponent.tertiaryButtonText
                            }
                        }
                        if (uiComponent.dismissButtonText != null) {
                            Button {
                                variant = mui.material.ButtonVariant.outlined
                                onClick = {
                                    uiComponent.onDismissButtonClick?.invoke()
                                    removeHeadMessage()
                                }
                                +uiComponent.dismissButtonText
                            }
                        }
                        if (uiComponent.confirmButtonText != null) {
                            Button {
                                variant = mui.material.ButtonVariant.contained
                                onClick = {
                                    uiComponent.onConfirmation()
                                    removeHeadMessage()
                                }
                                +uiComponent.confirmButtonText
                            }
                        }
                    }
                }
            }

            else -> Unit
        }

        if (isEmpty) {
            ScreenCenterMessageView {
                message = emptyMessage
            }
        }

        if (networkState is NetworkState.Failed && progressBarState == ProgressBarState.Idle && !hasReady) {
            ScreenErrorView {
                message = networkState.message ?: "加載失敗"
                this.onTryAgain = onTryAgain
            }
        }

        if (isLoading) {
            ScreenLoadingView {
                message = loadingMessage
            }
        }

        if (progressBarState is ProgressBarState.MessageLoading) {
            Dialog {
                open = true
                this.fullWidth = false
                onClose = { _, _ -> }
                DialogContent {
                    div {
                        css {
                            display = Display.flex
                            flexDirection = FlexDirection.column
                            alignItems = AlignItems.center
                            justifyContent = JustifyContent.center
                            padding = Padding(12.px, 16.px)
                            asDynamic().gap = "10px"
                        }
                        CircularProgress {
                            asDynamic().size = 28
                        }
                        div {
                            +(progressBarState.message.ifEmpty { "處理中..." })
                        }
                    }
                }
            }
        }
    }
}

private external interface ScreenCenterMessageViewProps : react.Props {
    var message: String
}

private val ScreenCenterMessageView = FC<ScreenCenterMessageViewProps> { props ->
    div {
        css {
            position = Position.fixed
            top = 0.px
            left = 0.px
            width = 100.vw
            height = 100.vh
            display = Display.flex
            alignItems = AlignItems.center
            justifyContent = JustifyContent.center
            asDynamic().color = "#777"
            zIndex = 1000.unsafeCast<ZIndex>()
        }
        +props.message
    }
}

private external interface ScreenLoadingViewProps : react.Props {
    var message: String
}

private val ScreenLoadingView = FC<ScreenLoadingViewProps> { props ->
    div {
        css {
            position = Position.fixed
            top = 0.px
            left = 0.px
            width = 100.vw
            height = 100.vh
            display = Display.flex
            alignItems = AlignItems.center
            justifyContent = JustifyContent.center
            background = "rgba(255,255,255,0.65)".unsafeCast<Background>()
            zIndex = 1500.unsafeCast<ZIndex>()
        }
        div {
            css {
                display = Display.flex
                flexDirection = FlexDirection.column
                alignItems = AlignItems.center
                asDynamic().gap = "12px"
//                padding = Padding(18.px, 24.px)
//                background = Color("#ffffff")
//                border = Border(1.px, LineStyle.solid, Color("#ededed"))
//                asDynamic().borderRadius = "10px"
            }
            CircularProgress {
                asDynamic().size = 30
            }
            div {
                +props.message
            }
        }
    }
}

private external interface ScreenErrorViewProps : react.Props {
    var message: String
    var onTryAgain: () -> Unit
}

private val ScreenErrorView = FC<ScreenErrorViewProps> { props ->
    div {
        css {
            position = Position.fixed
            top = 0.px
            left = 0.px
            width = 100.vw
            height = 100.vh
            display = Display.flex
            alignItems = AlignItems.center
            justifyContent = JustifyContent.center
            zIndex = 1000.unsafeCast<ZIndex>()
        }
        div {
            css {
                display = Display.flex
                flexDirection = FlexDirection.column
                alignItems = AlignItems.center
                asDynamic().gap = "12px"
                maxWidth = 460.px
                padding = Padding(12.px, 16.px)
                asDynamic().textAlign = "center"
            }
            div {
                css { asDynamic().color = "#e53935" }
                +props.message
            }
            Button {
                variant = mui.material.ButtonVariant.outlined
                onClick = { props.onTryAgain() }
                +"重試"
            }
        }
    }
}
